import { useState, useEffect } from "react";
import decode from "jwt-decode";
import * as userRoles from "../config/userRoles";

export const PERMISSIONS_UPDATED_EVENT = "tap-permissions-updated";

function getLabelToRoleIdMap() {
  if (!userRoles.ROLE_LABELS) {
    return {};
  }
  return Object.entries(userRoles.ROLE_LABELS).reduce((acc, [id, label]) => {
    const upper = label.toUpperCase();
    acc[upper] = id;
    acc[upper.replace(/\s+/g, "_")] = id;
    return acc;
  }, {});
}

/**
 * Normalizes JWT/localStorage permissions to role ID strings.
 * @param {string|string[]|number[]|null|undefined} raw
 * @returns {string[]}
 */
export function parsePermissionTokens(raw) {
  if (raw == null || raw === "") {
    return [];
  }

  const tokens = Array.isArray(raw)
    ? raw.map((value) => String(value).trim())
    : String(raw)
        .split(",")
        .map((token) => token.trim())
        .filter(Boolean);

  return tokens.map((token) => {
    if (userRoles.ROLE_LABELS[token]) {
      return token;
    }
    const upper = token.toUpperCase();
    const labelToRoleId = getLabelToRoleIdMap();
    return (
      labelToRoleId[upper] ??
      labelToRoleId[upper.replace(/\s+/g, "_")] ??
      token
    );
  });
}

function getRawPermissions() {
  const token = localStorage.getItem("token");
  if (token) {
    try {
      const decoded = decode(token);
      if (decoded?.permissions != null && decoded.permissions !== "") {
        return decoded.permissions;
      }
    } catch {
      // Fall back to localStorage below
    }
  }
  return localStorage.getItem("permissions");
}

/**
 * Retrieves user permissions from the JWT and localStorage.
 * @returns {{ isAdmin: boolean, isTapAdmin: boolean, isTapUser: boolean, isJobTracker: boolean, isDigitalStoresUser: boolean }}
 */
export const getPermissions = () => {
  const permissions = parsePermissionTokens(getRawPermissions());
  return {
    isAdmin: permissions.includes(userRoles.ROLE_ADMINISTRATOR),
    isTapAdmin: permissions.includes(userRoles.ROLE_TAP_ADMIN),
    isTapUser: permissions.includes(userRoles.ROLE_TAP_USER),
    isJobTracker: permissions.includes(userRoles.ROLE_JOB_TRACKER),
    isDigitalStoresUser: permissions.includes(userRoles.ROLE_DIGITAL_STORES),
  };
};

/**
 * React hook that returns user permissions and updates on storage changes.
 * @returns {{ isAdmin: boolean, isTapAdmin: boolean, isTapUser: boolean, isJobTracker: boolean, isDigitalStoresUser: boolean }}
 */
export function usePermissions() {
  const [perms, setPerms] = useState(getPermissions());

  useEffect(() => {
    const refresh = () => setPerms(getPermissions());
    const handleStorage = (e) => {
      if (e.key === "permissions" || e.key === "token") {
        refresh();
      }
    };
    window.addEventListener("storage", handleStorage);
    window.addEventListener(PERMISSIONS_UPDATED_EVENT, refresh);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(PERMISSIONS_UPDATED_EVENT, refresh);
    };
  }, []);

  return perms;
}

/**
 * Check if a feature flag is enabled
 * @param {string} flag - The feature flag name to check
 * @returns {boolean} Whether the flag is enabled
 */
export const hasFeature = (flag) => {
  const featureFlags = localStorage.getItem("feature-flags");
  if (!featureFlags) return false;
  return featureFlags.split(",").includes(flag);
};
